/**
 * Sign In Page
 * Kombinerar BankID-autentisering med email/password för styrelsemedlemmar
 */

import React, { useEffect, useState } from 'react';
import { Mail, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BankIDSignIn } from '../../components/BankIDSignIn';
import { auth, db } from '../../config/firebase';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { landingPathFor } from '../../utils/bankidLaunch';

const SignIn = () => {
  const navigate = useNavigate();

  // Följ Firebase: BankID-appen öppnar sajten som en NY sida när
  // legitimeringen är klar, medan fliken som startade inloggningen slutför
  // den i bakgrunden. Den nya sidan hamnar här, och utan lyssnare stannar
  // den på inloggningsknappen fast användaren är inloggad. Så fort en
  // inloggning dyker upp, från vilken flik som helst, går vi vidare.
  useEffect(() => {
    let active = true;
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) return;
      let role: string | undefined;
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        role = snap.exists() ? snap.data()?.role : undefined;
      } catch {
        role = undefined;
      }
      if (active) navigate(landingPathFor(role));
    });
    return () => { active = false; unsubscribe(); };
  }, [navigate]);

  // Email/password states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [showBoardLogin, setShowBoardLogin] = useState(false);

  // Handle board member email/password login
  const handleBoardLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);

      // Fetch user document from Firestore
      const userRef = doc(db, 'users', cred.user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        setError('Kunde inte hitta användarinfo.');
        return;
      }

      const userData = userSnap.data();

      // Route based on user role
      if (userData.role === 'brfadmin') {
        navigate('/dashboard');
      } else if (userData.role === 'hyria_admin') {
        navigate('/hyria-dashboard');
      } else {
        navigate('/');
      }
    } catch (err: any) {
      setError('Fel e-post eller lösenord.');
    } finally {
      setLoading(false);
    }
  };

  // Handle forgot password
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!forgotPasswordEmail.trim()) {
      setError('Ange en e-postadress.');
      return;
    }

    setForgotPasswordLoading(true);
    setError('');

    try {
      await sendPasswordResetEmail(auth, forgotPasswordEmail);
      setForgotPasswordSuccess(true);
      setError('');
    } catch (err: any) {
      if (err.code === 'auth/user-not-found') {
        setError('Ingen användare hittades med denna e-postadress.');
      } else {
        setError('Ett fel uppstod. Försök igen senare.');
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Handle BankID success
  const handleBankIDSuccess = () => {
    // Navigation is handled by useBankID hook
  };

  // Handle BankID error
  const handleBankIDError = (errorMessage: string) => {
    setError(errorMessage);
  };

  // If showing board login form
  if (showBoardLogin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-4">
        <div className="w-full max-w-md mx-auto">
          <div className="mb-8 text-center">
            <button
              onClick={() => {
                setShowBoardLogin(false);
                setError('');
                setEmail('');
                setPassword('');
              }}
              className="text-[#62B8E9] hover:text-[#4A9FD1] text-sm font-medium mb-4"
            >
              ← Tillbaka till BankID-inloggning
            </button>
            <h2 className="text-2xl font-bold text-zinc-800 mb-2">
              Styrelsemedlem? Logga in med e-post
            </h2>
            <p className="text-zinc-600 text-sm">
              Endast för BRF-administratörer och Hyria-personal
            </p>
          </div>

          {!showForgotPassword ? (
            <form className="space-y-4 mb-4" onSubmit={handleBoardLogin}>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">
                  <Mail className="w-5 h-5" />
                </span>
                <input
                  type="email"
                  placeholder="E-postadress"
                  className="w-full pl-10 pr-4 py-3 rounded-2xl border border-zinc-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-base bg-white"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  disabled={loading}
                />
              </div>

              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">
                  <Lock className="w-5 h-5" />
                </span>
                <input
                  type="password"
                  placeholder="Lösenord"
                  className="w-full pl-10 pr-4 py-3 rounded-2xl border border-zinc-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-base bg-white"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  disabled={loading}
                />
              </div>

              {error && <div className="text-red-600 text-sm text-center pt-2">{error}</div>}

              <button
                type="submit"
                className="w-full bg-black text-white rounded-2xl py-3 text-lg font-semibold shadow-md hover:bg-zinc-900 transition disabled:opacity-60"
                disabled={loading}
              >
                {loading ? 'Loggar in...' : 'Logga in'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-[#62B8E9] hover:text-[#4A9FD1] text-sm font-medium"
                >
                  Glömt lösenord?
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4 mb-4">
              {!forgotPasswordSuccess ? (
                <form onSubmit={handleForgotPassword}>
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold text-zinc-800 mb-2">Återställ lösenord</h3>
                    <p className="text-zinc-600 text-sm">
                      Ange din e-postadress så skickar vi dig en länk för att återställa lösenordet.
                    </p>
                  </div>

                  <div className="relative mb-4">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">
                      <Mail className="w-5 h-5" />
                    </span>
                    <input
                      type="email"
                      placeholder="E-postadress"
                      className="w-full pl-10 pr-4 py-3 rounded-2xl border border-zinc-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-base bg-white"
                      value={forgotPasswordEmail}
                      onChange={e => setForgotPasswordEmail(e.target.value)}
                      autoComplete="email"
                      required
                      disabled={forgotPasswordLoading}
                    />
                  </div>

                  {error && <div className="text-red-600 text-sm text-center pt-2 mb-4">{error}</div>}

                  <button
                    type="submit"
                    className="w-full bg-[#62B8E9] text-white rounded-2xl py-3 text-lg font-semibold shadow-md hover:bg-[#4A9FD1] transition disabled:opacity-60 mb-4"
                    disabled={forgotPasswordLoading}
                  >
                    {forgotPasswordLoading ? 'Skickar...' : 'Skicka återställningslänk'}
                  </button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotPasswordEmail('');
                        setError('');
                      }}
                      className="text-[#62B8E9] hover:text-[#4A9FD1] text-sm font-medium"
                    >
                      Tillbaka till inloggning
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center">
                  <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-4">
                    <h3 className="text-lg font-semibold text-green-800 mb-2">E-post skickad!</h3>
                    <p className="text-green-700 text-sm">
                      Vi har skickat en länk för att återställa lösenordet till <strong>{forgotPasswordEmail}</strong>
                    </p>
                    <p className="text-green-600 text-xs mt-2">
                      Kontrollera din e-post och klicka på länken för att sätta ett nytt lösenord.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail('');
                      setForgotPasswordSuccess(false);
                      setError('');
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Tillbaka till inloggning
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main BankID login view
  return (
    <div className="min-h-screen bg-white">
      {/* BankID Sign In Component */}
      <BankIDSignIn
        onSuccess={handleBankIDSuccess}
        onError={handleBankIDError}
      />

      {/* Board member login link */}
      <div className="fixed bottom-8 left-0 right-0 flex justify-center">
        <button
          onClick={() => setShowBoardLogin(true)}
          className="bg-white border border-zinc-200 text-zinc-700 px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all font-medium"
        >
          Är du styrelsemedlem? Logga in här
        </button>
      </div>
    </div>
  );
};

export default SignIn;
